import type { ButtonProps } from '@mantine/core';
import {
  Badge,
  Button,
  Group,
  Tooltip,
  createPolymorphicComponent,
  useMantineTheme,
  Text,
} from '@mantine/core';
import { IconBolt, IconDownload } from '@tabler/icons-react';
import { forwardRef } from 'react';
import { JoinPopover } from '~/components/JoinPopover/JoinPopover';
import { abbreviateNumber } from '~/utils/number-helpers';

const _DownloadButton = forwardRef<HTMLButtonElement, Props>(
  (
    {
      iconOnly,
      canDownload,
      downloadPrice,
      listedPrice,
      children,
      tooltip,
      joinAlert,
      ...buttonProps
    },
    ref
  ) => {
    const theme = useMantineTheme();
    // `downloadPrice` is what THIS viewer must pay; `listedPrice` is what buyers pay, shown to the
    // owner who already has access and would otherwise see no price at all on their own model.
    const shownPrice = downloadPrice ?? listedPrice;
    const isListedOnly = downloadPrice == null && listedPrice != null;
    const priceBadge = (
      <Badge
        radius="sm"
        size="sm"
        variant="filled"
        color="yellow.7"
        style={{
          position: 'absolute',
          top: '-8px',
          right: '-8px',
          boxShadow: theme.shadows.sm,
          padding: '4px 2px',
          paddingRight: '6px',
        }}
      >
        <Group gap={0} wrap="nowrap">
          <IconBolt style={{ fill: theme.colors.dark[9] }} color="dark.9" size={14} />{' '}
          <Text size="xs" fz={11} c="dark.9">
            {abbreviateNumber(shownPrice ?? 0)}
          </Text>
        </Group>
      </Badge>
    );
    const purchaseIcon = isListedOnly ? (
      <Tooltip label={`Buyers pay ${abbreviateNumber(listedPrice ?? 0)} Buzz`} withArrow>
        {priceBadge}
      </Tooltip>
    ) : (
      priceBadge
    );

    const button = iconOnly ? (
      <Tooltip label={tooltip ?? 'Download options'} withArrow>
        <Button
          pos="relative"
          className="overflow-visible"
          ref={ref}
          {...buttonProps}
          variant="light"
        >
          <IconDownload size={24} />
          {!!shownPrice && <>{purchaseIcon}</>}
        </Button>
      </Tooltip>
    ) : (
      <Button pos="relative" className="overflow-visible" ref={ref} {...buttonProps}>
        <Group gap={8} wrap="nowrap">
          <IconDownload size={20} />
          {!!shownPrice && <>{purchaseIcon}</>}
          {children}
        </Group>
      </Button>
    );

    return canDownload || (downloadPrice ?? 0) > 0 ? (
      button
    ) : (
      <JoinPopover message={joinAlert ?? 'You need to be a member to start the download'}>
        {button}
      </JoinPopover>
    );
  }
);
_DownloadButton.displayName = 'DownloadButton';

type Props = ButtonProps & {
  iconOnly?: boolean;
  canDownload?: boolean;
  downloadPrice?: number;
  /** What buyers pay. Informational only — shown to the owner/mod, never gates the button. */
  listedPrice?: number;
  modelVersionId?: number;
  tooltip?: string;
  joinAlert?: string;
};

export const DownloadButton = createPolymorphicComponent<'button', Props>(_DownloadButton);
